const { debounce, getElement } = window.BAO.Utils

class LazyVideo extends window.BAO.CustomElement() {
  static activeUnmutedVideo = null

  connectedCallback () {
    this.video = getElement('video', { context: this })

    super.connectedCallback()

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })

    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video)
  }

  setupListeners () {
    super.setupListeners()

    this.toggleMute = this.toggleMute.bind(this)
    this.togglePlayback = this.togglePlayback.bind(this)

    this.els?.mute?.elements.forEach((muteEl) => {
      this.listeners.add(muteEl, 'click', this.toggleMute)
    })

    this.els?.pause?.elements.forEach((pauseEl) => {
      this.listeners.add(pauseEl, 'click', this.togglePlayback)
    })

    this.listeners.add(this.video, 'pause', () => {
      this.setAttribute('data-paused', true)
    })

    this.listeners.add(this.video, 'play', () => {
      this.setAttribute('data-paused', false)
    })

    if (this.breakpoint) {
      this.onResize = this.onResize.bind(this)

      this.debouncedResize = debounce((event) => {
        this.onResize(event)
      }, 240)

      this.listeners.add(window, 'resize', this.debouncedResize)
    }
  }

  toggleMute () {
    const isCurrentlyMuted = this.video.muted

    // If this video is about to be unmuted, mute the previously unmuted one first
    if (
      isCurrentlyMuted &&
      LazyVideo.activeUnmutedVideo &&
      LazyVideo.activeUnmutedVideo !== this
    ) {
      LazyVideo.activeUnmutedVideo.setMuted(true)
    }

    this.setMuted(!isCurrentlyMuted)

    if (this.video.muted === false) {
      LazyVideo.activeUnmutedVideo = this
    }
  }

  setMuted (muted) {
    this.video.muted = muted
    this.setAttribute('data-muted', muted)

    if (muted && LazyVideo.activeUnmutedVideo === this) {
      LazyVideo.activeUnmutedVideo = null
    }
  }

  togglePlayback () {
    if (this.video.paused) {
      this.video.play()
      return
    }

    this.video.pause()
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      this.video.removeAttribute('autoplay')
      this.video.pause()
      this.video.setAttribute('controls', '')
    }

    if (this.video.dataset?.poster) {
      this.video.setAttribute('poster', this.video.dataset.poster)
    }

    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (
        typeof videoSource.tagName === 'string' &&
        videoSource.tagName === 'SOURCE'
      ) {
        videoSource.src = videoSource.dataset.src
      }
    }

    this.video.load()
    observer.unobserve(this.video)

    this.setAttribute('loaded', '')
    this.setAttribute('data-muted', this.video.muted)

    if (
      window.BAO.MediaBreakpoints.isSm ||
      this.video.hasAttribute('autoplay') === false
    ) {
      return
    }

    if (this.video.paused) {
      document.addEventListener('touchstart', () => {
        if (this.video.paused) {
          this.video.play()
        }
      })
    }
  }

  onResize () {
    if (window.BAO.MediaBreakpoints.isSm && this.breakpoint !== 'desktop') {
      this.setMuted(true)
      return
    }

    this.setMuted(true)
  }

  get breakpoint () {
    return this.dataset.breakpoint || ''
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}
